
<template>
  <div class="w-full max-w-xs sm:max-w-md mx-auto py-2 px-4 rounded-lg">
    <div class="bg-green-500 text-white px-4 py-2 rounded-t-md text-center font-bold text-xl">
      Get a Free Property Cash Offer
    </div>
    <div class="bg-green-500 text-white px-4 py-1 text-center font-semibold text-sm">
      No-Obligation - Instant Decision<br>Get a Quick Quote Now
    </div>
    <form @submit.prevent="handleSubmit" class="bg-white shadow-md rounded-b-md px-6 sm:px-8 pt-6 pb-4">
      <div class="mb-4">
        
        <!-- STEP 1: Postcode Entry with Autocomplete -->
        <div v-if="!addressSelected">
          <label class="block text-gray-700 font-bold mb-2 text-lg" for="postcode">
            Get Started...
          </label>
          
          <!-- Postcode Input -->
          <div class="flex">
            <input 
              v-model="postcode" 
              @input="handlePostcodeInput"
              @focus="showSuggestions = true"
              class="mobile-input rounded-full bg-gray-200 border-2 border-blue-300 w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:border-blue-500 text-lg" 
              id="postcode" 
              type="text" 
              placeholder="Enter your Postcode or Address..."
              autocomplete="off"
            />
            <button 
              v-if="!isLoadingPostcode && postcode.trim()"
              type="button"
              @click="validatePostcode"
              class="submit-btn bg-green-500 hover:bg-green-600 text-white px-6 rounded-full font-bold transition duration-300 ease-in-out transform hover:scale-105"
            >GO</button>
            <div v-if="isLoadingPostcode" class="flex items-center ml-2">
              <span class="text-gray-500 text-sm">Loading...</span>
            </div>
          </div>
          
          <!-- Postcode Error -->
          <div v-if="postcodeError" class="text-red-500 text-sm mt-2">{{ postcodeError }}</div>
          
          <!-- Address Suggestions Dropdown -->
          <div class="relative">
            <ul v-if="showSuggestions && addressSuggestions.length > 0" class="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-64 overflow-y-auto">
              <li 
                v-for="(suggestion, index) in addressSuggestions" 
                :key="index"
                @click="selectAddress(suggestion)"
                class="py-3 px-4 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div class="text-gray-700 text-sm">{{ suggestion.address }}</div>
                <div class="text-gray-500 text-xs mt-1">{{ suggestion.postcode }}</div>
              </li>
            </ul>
          </div>
          
          <!-- API Failed - Manual Entry Option -->
          <div v-if="apiFailedMessage" class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
            <p class="text-yellow-800 text-sm mb-2">{{ apiFailedMessage }}</p>
            <button 
              type="button" 
              @click="showManualEntry = true"
              class="text-blue-600 hover:text-blue-800 text-sm font-semibold"
            >Enter Address Manually →</button>
          </div>
          
          <!-- No Results Message -->
          <div v-if="showSuggestions && postcode.trim().length >= 2 && addressSuggestions.length === 0 && !isLoadingPostcode && !apiFailedMessage" class="text-gray-500 text-sm mt-2">
            No addresses found. Try checking your postcode or use manual entry.
          </div>
        </div>
        
        <!-- STEP 1B: Manual Address Entry Fallback -->
        <div v-if="showManualEntry && !addressSelected">
          <div class="flex justify-between items-center mb-3">
            <p class="text-gray-700 font-bold text-lg">Manual Address Entry</p>
            <button 
              type="button" 
              @click="backToAutocomplete"
              class="text-blue-600 hover:text-blue-800 text-sm"
            >← Back</button>
          </div>
          
          <label class="block text-gray-700 font-semibold mb-1 text-sm" for="street-address">Street Address</label>
          <input 
            v-model="manualStreetAddress" 
            id="street-address" 
            type="text" 
            placeholder="Enter your street address..."
            class="rounded-lg bg-gray-200 border-2 border-blue-300 w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:border-blue-500 text-lg mb-3"
          />
          <div v-if="manualStreetAddressError" class="text-red-500 text-sm mb-2">{{ manualStreetAddressError }}</div>
          
          <label class="block text-gray-700 font-semibold mb-1 text-sm" for="city">City/Town</label>
          <input 
            v-model="manualCity" 
            id="city" 
            type="text" 
            placeholder="Enter your city or town..."
            class="rounded-lg bg-gray-200 border-2 border-blue-300 w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:border-blue-500 text-lg mb-3"
          />
          <div v-if="manualCityError" class="text-red-500 text-sm mb-2">{{ manualCityError }}</div>
          
          <button 
            type="button" 
            @click="submitManualAddress"
            class="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-full font-bold text-lg transition duration-300 ease-in-out transform hover:scale-105"
          >Continue →</button>
        </div>
        
        <!-- STEP 2: Selected Address Confirmation -->
        <div v-if="addressSelected">
          <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p class="text-green-800 font-bold mb-1">Address Selected</p>
            <p class="text-green-700 text-sm">{{ selectedAddressDisplay }}</p>
            <button 
              type="button" 
              @click="changeAddress"
              class="text-blue-600 hover:text-blue-800 text-sm mt-2"
            >Change Address</button>
          </div>
          
          <button 
            type="submit"
            class="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-full font-bold text-lg transition duration-300 ease-in-out transform hover:scale-105"
          >Continue →</button>
        </div>
      </div>
      
      <div class="review-section border-t-2 border-gray-200 mt-6 pt-6">
        <div class="flex flex-col items-center justify-center">
          <p class="review-text text-gray-600 italic text-center text-sm sm:text-lg">{{ reviewText }}</p>
          <div class="star-rating-logo-wrapper flex flex-col items-center justify-center mt-0">
            <div class="star-rating text-yellow-400 text-2xl">
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
            </div>
            <img :src="require('@/assets/rfline.png')" alt="Referenceline Logo" class="h-10 mt-0" />
          </div>
          <p class="reviewer-info text-gray-500 text-lg mt-2 text-center">{{ reviewerName }}</p>
        </div>
      </div>
      <div class="trust-logos mt-4">
        <img src="@/assets/trust-logos.png" alt="Trust Logos" class="mx-auto" style="width: 100%; max-width: 300px; height: auto;">
      </div>
    </form>
  </div>
</template>

<script>
import { postcodeService } from "../services/postcodeService";

export default {
  data() {
    return {
      postcode: "",
      addressSuggestions: [],
      showSuggestions: false,
      isLoadingPostcode: false,
      selectedAddress: null,
      addressSelected: false,
      submitted: false,
      postcodeError: "",
      debounceTimer: null,
      // Manual entry fallback
      showManualEntry: false,
      apiFailedMessage: "",
      manualStreetAddress: "",
      manualCity: "",
      manualStreetAddressError: "",
      manualCityError: "",
    };
  },
  
  computed: {
    selectedAddressDisplay() {
      if (this.selectedAddress) {
        return `${this.selectedAddress.address}, ${this.selectedAddress.postcode}`;
      }
      return "";
    },
    reviewText() {
      if (this.$route.path.startsWith('/financial/')) {
        return "From start to finish they clearly explained everything in detail i needed to know kept in touch with emails fone calls n txts they took care of every little detail and made my sale pass with ease i recommend them 100% for a quick sale it was a cash buy and the money was in my bank in days";
      } else {
        return "From start to finish they clearly explained everything in detail i needed to know kept in touch with emails fone calls n txts they took care of every little detail and made my sale pass with ease i recommend them 100% for a quick sale it was a cash buy and the money was in my bank in days";
      }
    },
    reviewerName() {
      if (this.$route.path.startsWith('/financial/')) {
        return "- Happy Seller";
      } else {
        return "Happy Seller";
      }
    },
  },
  
  methods: {
    handlePostcodeInput() {
      this.postcodeError = "";
      this.addressSuggestions = [];
      this.apiFailedMessage = "";
      
      if (this.postcode.trim().length < 2) {
        this.showSuggestions = false;
        return;
      }

      // Debounce the search (300ms)
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.searchAddresses();
      }, 300);
    },

    async searchAddresses() {
      this.isLoadingPostcode = true;
      this.postcodeError = "";
      this.apiFailedMessage = "";

      try {
        const query = this.postcode.trim();
        console.log('[AddressForm] searchAddresses called with query:', query);
        
        const response = await postcodeService.searchAddresses(query, 10);
        console.log("[AddressForm] Postcode API Response received:", response);

        if (response.result && response.result.hits && response.result.hits.length > 0) {
          console.log('[AddressForm] Found', response.result.hits.length, 'addresses');
          
          this.addressSuggestions = response.result.hits.map(hit => {
            const address = hit.address || hit.suggestion || `${hit.postcode}`;
            
            // Extract postcode from multiple sources
            let extractedPostcode = hit.postcode;
            
            if (!extractedPostcode && address) {
              // Try to extract UK postcode from address string
              const postcodeMatch = address.match(/[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}/i);
              if (postcodeMatch) {
                extractedPostcode = postcodeMatch[0].toUpperCase();
              }
            }
            
            // Fallback to user's input if still no postcode
            if (!extractedPostcode) {
              extractedPostcode = this.postcode.trim().toUpperCase();
            }
            
            console.log("[AddressForm] Processing hit:", hit, "Postcode:", extractedPostcode);
            
            return {
              address: address,
              postcode: extractedPostcode,
              id: hit.id,
              fullHit: hit,
            };
          });
          
          // Sort addresses by street number (numeric order)
          this.addressSuggestions.sort((a, b) => {
            // Extract leading number from address
            const numA = parseInt((a.address.match(/^\d+/) || ['0'])[0], 10);
            const numB = parseInt((b.address.match(/^\d+/) || ['0'])[0], 10);
            return numA - numB;
          });
          
          this.showSuggestions = true;
          this.apiFailedMessage = "";
          console.log("[AddressForm] Found addresses:", this.addressSuggestions);
        } else {
          console.log("[AddressForm] No results found");
          this.addressSuggestions = [];
          this.showSuggestions = false;
        }
      } catch (error) {
        console.error("[AddressForm] Error fetching addresses:", error.message);
        
        // Trigger manual entry fallback
        this.apiFailedMessage = "Address lookup service is temporarily unavailable. You can enter your address manually below.";
        this.postcodeError = "";
        this.addressSuggestions = [];
        this.showSuggestions = false;
      } finally {
        this.isLoadingPostcode = false;
      }
    },

    selectAddress(suggestion) {
      console.log("Selected address:", suggestion);
      
      const displayAddress = suggestion.address || suggestion.postcode || 'Unknown Address';
      
      this.selectedAddress = {
        ...suggestion,
        address: displayAddress,
      };
      
      this.addressSelected = true;
      this.showSuggestions = false;
      this.postcode = suggestion.postcode;
      this.postcodeError = "";
    },

    changeAddress() {
      this.addressSelected = false;
      this.selectedAddress = null;
      this.postcode = "";
      this.addressSuggestions = [];
      this.showSuggestions = false;
      this.postcodeError = "";
      this.showManualEntry = false;
    },

    validatePostcode() {
      this.postcodeError = "";
      
      if (!this.postcode.trim()) {
        this.postcodeError = "Please enter a postcode or address";
        return;
      }

      if (this.addressSuggestions.length > 0) {
        this.selectAddress(this.addressSuggestions[0]);
      } else {
        // If no suggestions, trigger manual entry
        this.apiFailedMessage = "No addresses found. You can enter your address manually below.";
      }
    },

    handleSubmit() {
      this.submitted = true;

      if (!this.selectedAddress) {
        this.postcodeError = "Please select an address";
        return;
      }

      // Parse the address to extract street and locality
      const addressParts = this.selectedAddress.address.split(',').map(part => part.trim());
      const street = addressParts.slice(0, -1).join(', ') || this.selectedAddress.address;
      
      // Use city from manual entry if available, otherwise extract from address
      const city = this.selectedAddress.city || addressParts[addressParts.length - 1] || '';

      // Emit the address data
      const addressData = {
        street: street,
        localityTownCounty: city,
        postcode: this.selectedAddress.postcode,
        country: "United Kingdom",
      };

      console.log("[AddressForm] Address submitted:", addressData);
      console.log("[AddressForm] Postcode being sent:", addressData.postcode);
      
      this.$emit("addressSelected", addressData);
      this.$emit('proceedToQuiz');
      window.scrollTo({
        top: 500,
        left: 0,
        behavior: 'smooth'
      });
    },

    backToAutocomplete() {
      this.showManualEntry = false;
      this.manualStreetAddress = "";
      this.manualCity = "";
      this.manualStreetAddressError = "";
      this.manualCityError = "";
      this.apiFailedMessage = "";
    },

    submitManualAddress() {
      this.manualStreetAddressError = "";
      this.manualCityError = "";

      if (!this.manualStreetAddress.trim()) {
        this.manualStreetAddressError = "Please enter your street address";
        return;
      }

      if (!this.manualCity.trim()) {
        this.manualCityError = "Please enter your city or town";
        return;
      }

      // Create address object for manual entry
      const manualAddress = {
        address: this.manualStreetAddress.trim(),
        postcode: this.postcode.trim().toUpperCase(),
        city: this.manualCity.trim(),
        id: "manual-entry",
      };

      this.selectAddress(manualAddress);
    },
  },
};
</script>




<style scoped>
/* Base styles for the form container */
.w-full {
  max-width: 100%;
}

/* Padding for better spacing on all devices */
.py-6 {
  padding: 2rem 1rem;
}

/* Form header styles */
.bg-green-500 {
  background-color: #38a169; /* Example green color */
}

.text-white {
  color: white;
}

/* Postcode input and button styles */
.mobile-input,
.submit-btn {
  padding: 0.5rem 1rem;
  font-size: 1rem;
  border-radius: 9999px; /* Full roundness */
}

/* Button-specific styles */
.submit-btn {
  background-color: #48bb78; /* Example green color for the button */
  color: white;
  margin-left: 0.5rem; /* Space between the input and button */
}

/* Review section styles */
.review-section {
  background-color: #f7f7f7;
  border-radius: 10px;
  padding: 2rem; /* Increased padding for larger quotes */
  margin-top: 2rem;
  border-top: 2px solid #e2e8f0;
  position: relative;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Pseudo-elements for quotes */
.review-section:before,
.review-section:after {
  color: #cbd5e0; /* Light color for the quote icon */
  position: absolute;
  font-family: "Times New Roman", Times, serif;
  z-index: 0;
}

.review-section:before {
  content: '“';
  top: 0;
  left: 0.5rem;
  font-size: 4em;
  line-height: 1;
}

.review-section:after {
  content: '”';
  bottom: 0;
  right: 0.5rem;
  font-size: 4em;
  line-height: 1;
}

/* Text styles for review and rating */
.review-text {
  font-style: italic;
  color: #4a5568; /* Darker color for contrast */
  line-height: 1.6; /* Adjust line height for better readability */
  max-width: 925px; /* Maximum width of the review text block */
  margin: auto; /* Center the text block */
}

/* Star rating styles */
.star-rating {
  font-size: 1.25rem;
  color: #ecc94b; /* Gold color for stars */
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
  margin-top: 1rem; /* Add space above the star rating */
}

.reviewer-info {
  font-size: 0.9rem; /* Smaller font size for meta information */
  color: #718096; /* Muted color for less emphasis */
  text-align: right; /* Align to the right */
  position: relative;
  z-index: 1;
}

/* Media query for mobile devices */
@media only screen and (max-width: 600px) {
  /* Adjust the main container padding */
  .w-full.max-w-xs.sm\:max-w-md.mx-auto.py-2.px-4.rounded-lg {
    padding: 1rem; /* Consistent padding on mobile */
  }
  .review-text {
    /* Adjust maximum width for smaller screens */
    max-width: 100%;
  }

  /* Adjust the form's overall padding to be consistent */
  .bg-white.shadow-md.rounded-b-md.px-6.sm\:px-8.pt-6.pb-4 {
    padding: 1rem; /* Adds padding inside the form */
  }

  /* Styles for input and button to ensure they do not touch the container edges */
  .mobile-input {
    flex-grow: 1; /* Input will grow to take available space */
    max-width: calc(100% - 32px); /* Adjust based on the main container's padding */
  }

  .submit-btn {
    flex-shrink: 0; /* Button will not shrink */
    width: 50px; /* Set a fixed width for the button */
  }

  /* Adjust the review text size for better readability */
  .review-text,
  .reviewer-info,
  .star-rating i {
    font-size: 0.775rem;
  }
}
</style>

